import React from "react";

import Player from "@vimeo/player";

import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {zIndexClasses} from "@client/style";

import {Image} from "@client/components/Image";
import {Layout} from "@client/site/Layout";
import * as components from "@client/site/components";
import {useStaticURL} from "@client/site/context";
import {classNames, reverse, server} from "@reactivated";

import {Grid} from "./ClientManagerPlanner";

export const Template = ({experiment_group}: server.core.templates.BusinessPage) => {
    const staticURL = useStaticURL();
    const iframeElement = React.useRef<HTMLIFrameElement>(null);
    const didPlayVideo = React.useRef(false);

    React.useEffect(() => {
        const player = new Player(iframeElement.current!);

        player.on("play", () => {
            didPlayVideo.current = true;
            void analytics.track("PLAYED_BUSINESS_VIDEO");
        });
        player.on("pause", () => {
            void analytics.track("PAUSED_BUSINESS_VIDEO");
        });
        player.on("ended", () => {
            void analytics.track("ENDED_BUSINESS_VIDEO");
        });

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        window.addEventListener("beforeunload", async () => {
            if (didPlayVideo.current === true && (await player.getEnded()) === false) {
                void analytics.track("DID_NOT_FINISH_BUSINESS_VIDEO");
            }
        });
    }, []);
    const background = (
        <div
            className={`
                absolute top-[-200px] left-0
                ${zIndexClasses.siteBackground}
                h-[830px] w-full skew-y-6 bg-[#D7EDF8]
                md:top-[-280px] md:skew-y-2
                lg:top-[-200px]
            `}
        >
            <div className="relative mx-auto size-full max-w-[1200px] overflow-hidden">
                <Image
                    className="
                        absolute left-[100px] w-[940px]
                        md:left-[300px]
                    "
                    image="circles"
                    title="Decorative circles"
                />
            </div>
        </div>
    );

    const metaImage = `${staticURL}images/og-business.png`;

    const showPricing = experiment_group === "PRICING";

    return (
        <Layout
            isBusiness
            metaImage={metaImage}
            title={{
                page: "Joy for Business - Track what your clients eat. Quickly.",
                meta: "Joy for Business",
            }}
            background={background}
        >
            <div
                className={classNames(
                    "business-hero",
                    "grid px-[10px] leading-[1.2] grid-cols-[1fr] gap-y-[20px] md:mt-[30px] md:mb-[50px] md:grid-cols-[44%_1fr] md:px-5",
                )}
            >
                <div
                    className="
                        flex flex-col gap-[14px] pt-5 text-center
                        md:gap-[30px] md:text-left
                    "
                >
                    <h1 style={{fontSize: 34, lineHeight: "3rem"}}>
                        Track what your clients eat.
                        <br />
                        <span style={{color: "#EF8165"}}>Quickly.</span>
                    </h1>
                    <p style={{fontSize: 18}}>
                        Joy Health Tracker is the turn-key nutrition platform you need.
                    </p>
                    <p style={{fontSize: 18}}>
                        Get detailed reports of all your clients in one place.
                    </p>
                    <div
                        className="
                            flex justify-center
                            md:justify-start
                        "
                    >
                        <typography.ButtonGroup>
                            {showPricing && (
                                <typography.Button
                                    track="get_started_top"
                                    variant="indigo"
                                    href={reverse("manager_registration")}
                                >
                                    Get Started
                                </typography.Button>
                            )}
                            <typography.Button
                                track="demo_cta_top"
                                variant="indigo"
                                href={reverse("business_demo")}
                            >
                                Schedule My Demo
                            </typography.Button>
                            <typography.Button
                                track="learn_more"
                                variant="text"
                                href="#about"
                            >
                                Learn more →
                            </typography.Button>
                        </typography.ButtonGroup>
                    </div>
                </div>
                <div>
                    <Image
                        image="business-hero"
                        title="Client dashboard, phone and client breakdown"
                    />
                </div>
            </div>
            {/*
            <div className="flex flex-col items-center m-[4vw_0] text-center space-y-[30px]">
                <typography.Subheader>
                    Free yourself from managing individual client food logs, tedious
                    spreadsheets and inaccurate calculations.
                </typography.Subheader>
            </div>
            */}

            {showPricing && (
                <div
                    className="
                        business-pricing mt-[100px] flex flex-wrap justify-between
                        gap-[40px]
                        md:flex-nowrap md:gap-0
                    "
                >
                    <div
                        className="
                            business-plan relative flex min-h-[200px] flex-col
                            items-center space-y-[15px] overflow-hidden rounded-lg
                            bg-white p-[30px_20px_20px_20px] text-center
                            shadow-[0_2px_4px_0_rgba(205,213,221,0.45)]
                        "
                    >
                        <typography.Subheader>
                            I'd like to try <br />
                            Joy
                        </typography.Subheader>
                        <typography.P>
                            One client. No time limits, no bills. <br />
                            The perfect way to get started.
                        </typography.P>
                        <div className="business-price text-lg">
                            <span>$0</span> per month
                        </div>
                        <div
                            className="
                                flex justify-center
                                md:justify-start
                            "
                        >
                            <typography.Button
                                track="start_trial"
                                href={reverse("manager_registration")}
                            >
                                Start My Trial
                            </typography.Button>
                        </div>
                    </div>
                    <div
                        className="
                            business-plan relative flex min-h-[200px] flex-col
                            items-center space-y-[15px] overflow-hidden rounded-lg
                            bg-white p-[30px_20px_20px_20px] text-center
                            shadow-[0_2px_4px_0_rgba(205,213,221,0.45)]
                        "
                    >
                        <typography.Subheader>
                            I manage a few clients
                        </typography.Subheader>
                        <typography.P>
                            For a small business getting started. <br />
                            No annual commitments.
                        </typography.P>
                        <div className="business-price text-lg">
                            <span>$50</span> per month
                        </div>
                        <div
                            className="
                                flex justify-center
                                md:justify-start
                            "
                        >
                            <typography.Button
                                track="get_started_pricing"
                                href={reverse("manager_registration")}
                            >
                                Get Started
                            </typography.Button>
                        </div>
                    </div>
                    <div
                        className="
                            business-plan relative flex min-h-[200px] flex-col
                            items-center space-y-[15px] overflow-hidden rounded-lg
                            bg-white p-[30px_20px_20px_20px] text-center
                            shadow-[0_2px_4px_0_rgba(205,213,221,0.45)]
                        "
                    >
                        <typography.Subheader>
                            I manage many clients
                        </typography.Subheader>
                        <typography.P>
                            For organizations with many clients or multiple staff
                            members.
                        </typography.P>
                        <div className="business-price text-lg">
                            <span>Let's talk</span>
                        </div>
                        <div
                            className="
                                flex justify-center
                                md:justify-start
                            "
                        >
                            <typography.Button
                                track="demo_cta_consultation"
                                href={reverse("business_demo")}
                            >
                                Book Consultation
                            </typography.Button>
                        </div>
                    </div>
                </div>
            )}

            <div
                id="about"
                className="
                    business-features grid grid-cols-[1fr] gap-x-5 gap-y-[50px]
                    pt-[50px]
                "
            >
                <components.Highlight
                    variant="orange"
                    icon="0432-desktop"
                    title="Client Dashboard"
                    description="A consolidated view of your clients"
                    large
                >
                    <li>Track all client activity and compliance in one view.</li>
                    <li>
                        Get access to individual, detailed reports of daily nutrition,
                        meals, and targets.
                    </li>
                    <li>Review client compliance at-a-glance.</li>
                </components.Highlight>

                <Image
                    image="business-dashboard"
                    title="Screenshot of client dashboard"
                />

                <div className="business-macro-planning relative mx-[-10px] bg-[#FCFCFD]">
                    <div
                        className="
                            business-macro-planning-wrapper grid gap-[50px] pb-5
                            md:grid-cols-[50%_50%] md:items-center md:gap-0 md:pb-0
                        "
                    >
                        <Image
                            image="business-planner"
                            title="Screenshot of macro planning and schedule"
                        />
                        <div>
                            <components.Highlight
                                variant="blue"
                                icon="0394-calendar-full"
                                title="Macro planning"
                                description="Flexibility for you and your clients"
                                large
                            >
                                <li>
                                    Set personalized calorie and macro targets for each
                                    client.
                                </li>
                                <li>
                                    Vary macros per day, tailoring them to activity
                                    level and calendar changes.
                                </li>
                                <li>
                                    Select from target types that include calories,
                                    macros and just logging a food diary.
                                </li>
                            </components.Highlight>
                        </div>
                    </div>
                </div>
                <components.Highlight
                    variant="green"
                    icon="0507-graph"
                    title="Reports"
                    description="Visualize your clients' progress"
                    large
                >
                    <li>Access calculations and reports in real-time.</li>
                    <li>
                        Get valuable metrics like caloric intake, weight, and waist
                        size.
                    </li>
                    <li>
                        View exact measurements with trend lines for at-a-glance
                        analysis of your clients.
                    </li>
                </components.Highlight>

                <Image image="business-reports" title="Screenshot of reports" />
                <div
                    className="
                        business-check-ins relative mx-[-10px] bg-[#30394B]
                        p-[50px_10px]
                    "
                >
                    <div
                        className="
                            relative z-5 flex flex-col-reverse items-center gap-[50px]
                            p-[20px]
                            md:flex-row md:p-[50px_100px]
                        "
                    >
                        <Image
                            image="business-check-ins"
                            title="Screenshot of qualitative check-ins"
                        />
                        <div style={{flex: 1, color: "white"}}>
                            <components.Highlight
                                variant="teal"
                                icon="0794-thumbs-up"
                                title="Client Check-ins"
                                description="Get insight beyond macros"
                                large
                            >
                                <li>
                                    Select qualitative questions to be answered by your
                                    clients quickly and easily while using the app.
                                </li>
                                <li>Vary check-in schedule for each client.</li>
                                <li>
                                    View answers for each check-in in one view to
                                    identify trends and progress.
                                </li>
                            </components.Highlight>
                        </div>
                    </div>
                </div>
                <components.Highlight
                    variant="purple"
                    icon="custom-app-icon"
                    title="Joy Health Tracker"
                    description="A first-class experience for your clients"
                    large
                >
                    <li>
                        The Joy Health Tracker app is free for your clients, through
                        your personalized invitation.
                    </li>
                    <li>
                        Gone are the days of clients running around using different apps
                        — or worse, sending screen shots of hand written journals.
                    </li>
                    <li>
                        Everything is in one place for you <em>and</em> your client.
                    </li>
                </components.Highlight>
                <Image image="business-app" title="Screenshot of journal" />
            </div>
            <div className="mx-auto mt-5 max-w-[800px]">
                <typography.Header>Video Overview</typography.Header>
                <div className="business-frame relative p-[56.25%_0_0_0]">
                    <iframe
                        ref={iframeElement}
                        src="https://player.vimeo.com/video/460189368"
                        frameBorder="0"
                        allow="autoplay; fullscreen"
                        allowFullScreen
                    />
                </div>
            </div>
            <div className="m-[4vw_0_2vw_0] space-y-[40px]">
                <div
                    className="
                        m-[4vw_0] flex flex-col items-center space-y-[30px] text-center
                    "
                >
                    <typography.Header>Why Joy for Business?</typography.Header>
                    <typography.Subheader>
                        Whatever you are doing to help your clients with nutrition, Joy
                        will make it better — for you and them.
                    </typography.Subheader>
                    <typography.Button
                        track="demo_cta_bottom"
                        href={reverse("business_demo")}
                    >
                        Schedule My Demo
                    </typography.Button>
                    <typography.Header>The Competition</typography.Header>
                </div>
                <Grid columns={3}>
                    <components.Highlight
                        variant="orange"
                        icon="0812-menu"
                        title="MyFitnessPal"
                        description="Popularity is the hallmark of mediocrity"
                        large
                    >
                        <li>
                            No client dashboard or link to your clients. Manual sharing.
                        </li>
                        <li>Community-sourced foods are often inaccurate</li>
                        <li>Takes too long to log meals</li>
                        <li>
                            Privacy nightmare, your data is given away like hot cakes
                        </li>
                    </components.Highlight>
                    <components.Highlight
                        variant="blue"
                        icon="0812-menu"
                        title="Cronometer"
                        description="Jack of all trades, master of none"
                        large
                    >
                        <li>Overwhelming feature set</li>
                        <li>Hard to navigate — busy interface</li>
                        <li>Amount of information is distracting</li>
                        <li>Drastically different web and app versions</li>
                        <li>Clients cannot group their foods and entries</li>
                    </components.Highlight>
                    <components.Highlight
                        variant="green"
                        icon="0812-menu"
                        title="Excel"
                        description="Ruined the world economy in 2008. Should it ruin diets too?"
                        large
                    >
                        <li>Entirely manual and labor-intensive</li>
                        <li>No real-time view into client activity</li>
                        <li>
                            No accountability for clients, with set goals and macros
                        </li>
                        <li>
                            Your time is valuable. Spend less time with the tools and
                            more helping clients
                        </li>
                    </components.Highlight>
                </Grid>
            </div>
        </Layout>
    );
};
